// The failure, if any, that the page save bar names after a save attempt. The
// page's own `dirty` and `saving` drive the rest of the bar, so this only
// remembers how the last attempt ended — wherever a page is one form with one
// Save, it lives here rather than once per page.
//
// The outcome is set by the submit handler, not watched from a mutation: a
// mutation's error lingers past the edits it was about.
//
// Only a save the fields stopped is named here. One the server refused is a
// toast (toastMutationError), not the bar. A field failure belongs to the
// fields: the moment they are clean again, a bar still naming them is lying,
// so it goes.

import { useState } from "react";

export interface SaveBarBinding {
    /** The failure to show in the bar, if any. */
    errorMessage: string | undefined;
    /** Call as a submit begins — clears the previous outcome. */
    submitting: () => void;
    saved: () => void;
    /** The save never left the page — a field is wrong. Clears with the field. */
    blocked: (message: string) => void;
    /** Forget the last outcome, e.g. when Discard drops the edits it was about. */
    reset: () => void;
}

export interface UseSaveBarProps {
    /** The form currently has field errors. Only consulted to decide whether a
     *  `blocked` failure is still true. */
    invalid?: boolean;
}

export function useSaveBar({ invalid = false }: UseSaveBarProps = {}): SaveBarBinding {
    const [blocked, setBlocked] = useState<string | null>(null);

    return {
        errorMessage: blocked !== null && invalid ? blocked : undefined,
        submitting: () => setBlocked(null),
        saved: () => setBlocked(null),
        blocked: (message: string) => setBlocked(message),
        reset: () => setBlocked(null),
    };
}
